import React, { useEffect, useState } from 'react';
import { useButton } from 'react-aria';
import { usePopper } from 'react-popper';
import { Popover, PopoverProps } from './Popover';

function ButtonTrigger({ TriggerElement, setTriggerRef, openButtonProps }) {
  return React.cloneElement(TriggerElement, { ref: setTriggerRef, ...openButtonProps });
}

export type PopoverTriggerPlacement =
  | 'auto'
  | 'auto-start'
  | 'auto-end'
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'right'
  | 'right-start'
  | 'right-end'
  | 'left'
  | 'left-start'
  | 'left-end';

type PopoverPropsNoStyle = Pick<PopoverProps, 'isOpen' | 'children' | 'isDismissable' | 'className'>;

export interface PopoverTriggerProps extends PopoverPropsNoStyle {
  TriggerElement: React.ReactElement;
  children: React.ReactElement | string;
  popoverPlacement?: PopoverTriggerPlacement;
  popoverStrategy?: 'absolute' | 'fixed';
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function PopoverTrigger({
  TriggerElement,
  children,
  popoverPlacement = 'right',
  popoverStrategy = 'absolute',
  onOpenChange,
  isOpen,
  ...props
}: PopoverTriggerProps): React.ReactElement {
  const [popoverOpen, setPopoverOpen] = useState(isOpen);

  useEffect(() => {
    setPopoverOpen(isOpen);
  }, [isOpen]);

  const [triggerRef, setTriggerRef] = useState(null);
  const [popperRef, setPopperRef] = useState(null);

  const { buttonProps: openButtonProps } = useButton(
    {
      onPress: () => {
        let newState = true;
        if (props.isDismissable) {
          newState = !isOpen;
        }

        setPopoverOpen(newState);
        if (onOpenChange) {
          onOpenChange(newState);
        }
      },
    },
    triggerRef
  );

  const onClose = () => {
    setPopoverOpen(false);
    if (onOpenChange) {
      onOpenChange(false);
    }
  };

  const { styles, attributes } = usePopper(triggerRef, popperRef, {
    placement: popoverPlacement,
    strategy: popoverStrategy,
  });

  return (
    <>
      <ButtonTrigger TriggerElement={TriggerElement} setTriggerRef={setTriggerRef} openButtonProps={openButtonProps} />
      {popoverOpen && (
        <Popover {...attributes.popper} {...props} onClose={onClose} isOpen={popoverOpen} popperStyle={styles.popper} setRef={setPopperRef}>
          {children}
        </Popover>
      )}
    </>
  );
}
